import React, { useState, useEffect, useContext, useRef } from 'react';
import { StoreContext } from '../../Context/StoreContext';
import axios from 'axios';
import './ProfileModal.css';
import { toast } from 'react-toastify';
import { assets } from '../../assets/assets';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/modalUtils';
import { createPortal } from 'react-dom';

const ProfileModal = ({ isOpen, onClose }) => {
  const { token, url, setProfilePicture, formatImageUrl } = useContext(StoreContext);
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    phone: '',
    preferences: {
      emailNotifications: true,
      smsNotifications: false
    }
  });
  const [formData, setFormData] = useState({
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    preferences: {
      emailNotifications: true,
      smsNotifications: false
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const profilePicInputRef = useRef(null);
  
  // Add/remove modal-open class to body
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    // Cleanup function to ensure we remove the class when component unmounts
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  // Effect to manage body scroll locking
  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
    
    // Cleanup function to ensure we unlock scroll when component unmounts
    return () => {
      unlockBodyScroll();
    };
  }, [isOpen]);

  // Fetch user profile data when modal opens
  useEffect(() => {
    if (isOpen && token) {
      fetchUserData();
    }
  }, [isOpen, token]);
  
  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${url}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success && response.data.user) {
        const user = response.data.user;
        setUserData({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          profilePicture: user.profilePicture || '',
          preferences: {
            emailNotifications: user.preferences?.emailNotifications ?? true,
            smsNotifications: user.preferences?.smsNotifications ?? false
          }
        });
        
        setFormData({
          phone: user.phone || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
          preferences: {
            emailNotifications: user.preferences?.emailNotifications ?? true,
            smsNotifications: user.preferences?.smsNotifications ?? false
          }
        });
        
        // Set profile picture preview if user has one
        if (user.profilePicture) {
          setProfilePicPreview(formatImageUrl(user.profilePicture));
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile information');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.match('image.*')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }
      
      setProfilePicFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const uploadProfilePicture = async () => {
    if (!profilePicFile) return;
    
    try {
      setIsLoading(true);
      
      const formData = new FormData();
      formData.append('profilePicture', profilePicFile);
      
      const response = await axios.post(
        `${url}/api/user/upload-profile-picture`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        toast.success('Profile picture updated successfully');
        
        // Update user data with new profile picture
        if (response.data.profilePictureUrl) {
          // For local storage URLs, prepend the server URL if needed
          const picUrl = response.data.profilePictureUrl;
          const formattedUrl = formatImageUrl(picUrl);
          
          setProfilePicPreview(formattedUrl);
          setUserData(prev => ({
            ...prev,
            profilePicture: picUrl
          }));
          
          // Also update the profile picture in StoreContext
          setProfilePicture(formattedUrl);
        }
        
        // Clear file input
        setProfilePicFile(null);
        if (profilePicInputRef.current) {
          profilePicInputRef.current.value = '';
        }
      } else {
        toast.error(response.data.message || 'Failed to update profile picture');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error(error.response?.data?.message || 'Failed to upload profile picture');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handlePreferenceChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...(prev.preferences || { emailNotifications: true, smsNotifications: false }),
        [name]: checked
      }
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      // Upload profile picture first if one is selected
      if (profilePicFile) {
        await uploadProfilePicture();
      }
      
      // Password change
      if (showPasswordFields) {
        if (!formData.currentPassword) {
          toast.error('Current password is required');
          setIsLoading(false);
          return;
        }
        
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error('New passwords do not match');
          setIsLoading(false);
          return;
        }
        
        if (formData.newPassword.length < 6) {
          toast.error('New password must be at least 6 characters');
          setIsLoading(false);
          return;
        }
        
        const response = await axios.put(`${url}/api/user/update-password`, {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          toast.success('Password updated successfully');
          setFormData(prev => ({
            ...prev,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }));
          setShowPasswordFields(false);
          setIsEditing(false);
        }
      } else {
        // Profile update - only send phone and preferences
        const updateData = {};
        
        // Only include phone if it's provided and not empty
        if (formData.phone && formData.phone.trim() !== '') {
          updateData.phone = formData.phone.trim();
        }
        
        // Only include preferences if they're properly defined
        if (formData.preferences) {
          updateData.emailNotifications = !!formData.preferences.emailNotifications;
          updateData.smsNotifications = !!formData.preferences.smsNotifications;
        }
        
        console.log('Sending profile update:', updateData); // Debug log
        
        // Profile update
        const response = await axios.put(`${url}/api/user/profile`, updateData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          toast.success('Profile updated successfully');
          fetchUserData();
          setIsEditing(false);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
      console.error('Error updating profile:', error);
      // Log detailed error information
      if (error.response) {
        console.error('Error details:', error.response.data);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Return null if modal is not open
  if (!isOpen) return null;

  // Use createPortal to render the modal at the root level of the DOM
  return createPortal(
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={e => e.stopPropagation()}>
        <div className="profile-modal-header">
          <h2>My Profile</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="profile-content">
          {isLoading && !userData ? (
            <div className="loading">Loading profile...</div>
          ) : (
            <>
              <div className="profile-header">
                <div className="profile-avatar">
                  {profilePicPreview ? (
                    <img 
                      src={profilePicPreview} 
                      alt="Profile" 
                      className="avatar-img"
                    />
                  ) : (
                    userData.name ? userData.name.charAt(0).toUpperCase() : 'U'
                  )}
                </div>
                <div className="profile-title">
                  <h3>{userData.name}</h3>
                  <span className="user-since">DASHBASKET Customer</span>
                </div>
              </div>
              
              {!isEditing ? (
                <div className="profile-details">
                  <div className="profile-item">
                    <label>Name</label>
                    <div>{userData.name}</div>
                  </div>
                  
                  <div className="profile-item">
                    <label>Email</label>
                    <div>{userData.email}</div>
                  </div>
                  
                  <div className="profile-item">
                    <label>Phone</label>
                    <div>{userData.phone || 'Not provided'}</div>
                  </div>
                  
                  <div className="profile-item">
                    <label>Notification Preferences</label>
                    <div>
                      Email notifications: {userData.preferences?.emailNotifications ? 'Enabled' : 'Disabled'}<br />
                      SMS notifications: {userData.preferences?.smsNotifications ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                  
                  <div className="profile-actions">
                    <button 
                      className="btn-edit" 
                      onClick={() => setIsEditing(true)}
                    >
                      Edit Profile
                    </button>
                    <button 
                      className="btn-password" 
                      onClick={() => {
                        setIsEditing(true);
                        setShowPasswordFields(true);
                      }}
                    >
                      Change Password
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="profile-form">
                  {showPasswordFields ? (
                    <>
                      <div className="form-group">
                        <label htmlFor="currentPassword">Current Password</label>
                        <input
                          type="password"
                          id="currentPassword"
                          name="currentPassword"
                          value={formData.currentPassword}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="newPassword">New Password</label>
                        <input
                          type="password"
                          id="newPassword"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        <input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="profile-picture-section">
                        <label>Profile Picture</label>
                        <div className="profile-picture-container">
                          {profilePicPreview ? (
                            <img 
                              src={profilePicPreview} 
                              alt="Profile" 
                              className="profile-picture-preview" 
                            />
                          ) : (
                            <div className="no-picture">
                              {userData.name ? userData.name.charAt(0).toUpperCase() : 'U'}
                            </div>
                          )}
                        </div>
                        <div className="picture-upload-container">
                          <input
                            type="file"
                            id="profilePicture"
                            name="profilePicture"
                            accept="image/*"
                            onChange={handleProfilePicChange}
                            ref={profilePicInputRef}
                          />
                          <small>Recommended size: 200x200 pixels, Max: 2MB</small>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="name">Name</label>
                        <input
                          type="text"
                          id="name"
                          value={userData.name}
                          disabled
                        />
                        <small>Name cannot be changed. Contact support for assistance.</small>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                          type="email"
                          id="email"
                          value={userData.email}
                          disabled
                        />
                        <small>Email cannot be changed. Contact support for assistance.</small>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+1XXXXXXXXXX"
                        />
                        <small>Include country code (e.g., +1 for US)</small>
                      </div>
                      
                      <div className="form-group">
                        <label>Notification Preferences</label>
                        <div className="checkbox-group">
                          <input
                            type="checkbox"
                            id="emailNotifications"
                            name="emailNotifications"
                            checked={!!formData.preferences?.emailNotifications}
                            onChange={handlePreferenceChange}
                          />
                          <label htmlFor="emailNotifications">Email Notifications</label>
                        </div>
                        
                        <div className="checkbox-group">
                          <input
                            type="checkbox"
                            id="smsNotifications"
                            name="smsNotifications"
                            checked={!!formData.preferences?.smsNotifications}
                            onChange={handlePreferenceChange}
                          />
                          <label htmlFor="smsNotifications">SMS Notifications</label>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn-cancel"
                      onClick={() => {
                        setIsEditing(false);
                        setShowPasswordFields(false);
                        // Reset form data
                        if (userData) {
                          setFormData({
                            phone: userData.phone || '',
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: '',
                            preferences: {
                              emailNotifications: userData.preferences?.emailNotifications ?? true,
                              smsNotifications: userData.preferences?.smsNotifications ?? false
                            }
                          });
                        }
                        // Reset profile picture preview if changed but not saved
                        if (profilePicFile) {
                          setProfilePicFile(null);
                          if (userData.profilePicture) {
                            setProfilePicPreview(formatImageUrl(userData.profilePicture));
                          } else {
                            setProfilePicPreview(null);
                          }
                          if (profilePicInputRef.current) {
                            profilePicInputRef.current.value = '';
                          }
                        }
                      }}
                    >
                      Cancel
                    </button>
                    
                    <button 
                      type="submit" 
                      className="btn-save"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : showPasswordFields ? 'Update Password' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body // Render directly in the body element
  );
};

export default ProfileModal; 